import React from 'react';

/**
 * PageContainer — the canonical outer wrapper for every landing page.
 *
 * Pairs with {@link ./PageHeader.jsx PageHeader} to give every primary
 * page an identical padding rhythm and content-width ceiling. The
 * platform had drifted into multiple competing values:
 *
 *   • `padding: 24` (TeamPage)            — full-width, small gutter
 *   • `padding: '28px 32px', maxWidth: 1400, margin: '0 auto'` (Overview)
 *   • `padding: '40px',    maxWidth: 1400, margin: '0 auto'` (ApprovalCenter)
 *   • `padding: '22px 28px 0'` then no inner constraint (WorkflowIntelligence)
 *
 * That meant a buyer scrolling through pages saw the content jump
 * around horizontally — Overview centred at 1400px with big bands of
 * empty space, Team filling edge-to-edge, Approval Center with chunky
 * 40px gutters that wasted the right-side real estate. A premium
 * security/compliance product reads as one continuous canvas; this
 * container makes that the default.
 *
 * Tokens:
 *   • Gutter:  24px top / 32px sides — comfortable but not wasteful.
 *               Mirrors Linear, Vercel, Stripe dashboards.
 *   • Ceiling: 1600px — wide enough that 13"–27" laptop/desktop screens
 *               fill edge-to-edge, but constrained on 32"+ monitors so
 *               the content doesn't stretch into unreadable line-lengths.
 *   • Centred: `margin: 0 auto` so the constraint kicks in symmetrically.
 *
 * Usage:
 *
 *   <PageContainer>
 *     <PageHeader icon={...} title="..." subtitle="..." />
 *     ...rest of the page...
 *   </PageContainer>
 *
 * Override:
 *   • `wide`     — bumps maxWidth to 1800 for chart-heavy pages
 *                  (e.g. Workflow Intelligence percentile charts)
 *   • `narrow`   — caps at 1100 for form-heavy pages where line length
 *                  is the dominant readability concern (e.g. Policy Config)
 *   • `style`    — escape hatch; merged on top of defaults so a page can
 *                  tweak background or padding without recreating the
 *                  whole shell
 */
const TOKENS = {
  paddingY: 24,
  paddingX: 32,
  maxWidth: 1600,
  maxWidthWide:   1800,
  maxWidthNarrow: 1100,
};

export default function PageContainer({
  children,
  wide,
  narrow,
  style,
  className,
}) {
  const maxWidth = wide
    ? TOKENS.maxWidthWide
    : narrow
      ? TOKENS.maxWidthNarrow
      : TOKENS.maxWidth;

  return (
    <div
      className={className}
      style={{
        padding: `${TOKENS.paddingY}px ${TOKENS.paddingX}px`,
        maxWidth,
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
        ...style,
      }}
    >
      {children}
    </div>
  );
}
